import { useEffect } from "react";
// Import the useAuth hook to access the authentication state.
import { useAuth } from "react-oidc-context";
import { useLocation } from "react-router-dom";

const AuthErrorHandler: React.FC = () => {
  // Access the authentication object to retrieve user login status and methods like "signinSilent" which we will need later.
  const auth = useAuth();
  // Access the current location object (incl. the URL path) via useLocation().
  // It is vital to retrieve any query parameters like ?code to check if auth related info is present.
  const location = useLocation();

  useEffect(() => {
    // The .search property of the location object contains the query string part of the current URL - i.e., everything after the '?'.
    // The !auth.isLoading ensures that we don't intervene until the auth library has finished loading.
    if (location.search && !auth.isLoading) {
      // Check if the URL contains OIDC auth parameters like "code" or "state".
      const queryParams = location.search.includes("code=") || location.search.includes("state=");

      if (queryParams && auth.error?.message.includes("No matching state found")) {
        console.log("Detected back button navigation with an expired auth code");
        // Clean the URL first to avoid retrying with an invalid code parameter.
        // When a user hits the browser's 'back' button after they had just been logged in, the browser loads the exact URL history entry.
        // This entry would still have auth params like ?code.
        // The code in that URL is a one time use only which has already been used when the user initially logged in successfully.
        // The react-oidc-context library tries to process the same code to return an access token, but it fails and throws: "No matching state found in storage".
        // The replaceState is a method of the window.history API. It updates the current entry in the browser's history without reloading the page.
        // ReplaceState() takes 2-3 args: a state object, which you can use to store custom data with the history entry (in our case just an empty object), and a URL (which will be '/'). This is what we use to replace the current browser history entry.
        window.history.replaceState({}, "", "/");

        // Attempt silent re-authentication without page reload.
        // The auth.signinSilent() method is provided by the react-oidc-context library.
        if (!auth.isAuthenticated) {
          console.log("Attempting silent authentication.");
          auth.signinSilent().catch((err: unknown) => {
            // if the silent authentication fails, redirect the user to the login screen to log in again.
            console.error("Silent authentication has failed:", err);
            auth.signinRedirect().catch((redirectErr: unknown) => {
              console.error("Redirect to login failed:", redirectErr);
            });
          });
        }
      }
    }
  }, [auth, location]);
  // Return null since this component doesn't render any UI; it only runs 'side-effect' logic.
  return null;
};

export default AuthErrorHandler;
